import React, { useEffect, useState } from "react";
import { ProColumns } from "@ant-design/pro-table/es/typing";
import { Button, Select, Tag } from "antd";
import {
  PlusCircleOutlined,
  FileOutlined,
  DeleteOutlined,
  FolderOpenOutlined,
  FileTextOutlined,
  CheckCircleFilled,
  MinusOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import Table from "../../../Common/Table";
import clsx from "clsx";
import dayjs from "dayjs";
import ReadingModal from "./ReadingModal";
import {
  ReadingGetParams,
  ReadingTableRow,
  useReadingTableGet,
  useReadingTypesGet,
} from "../../../../hooks/api/readings";
import { SortOrder } from "antd/es/table/interface";
import { RequestData } from "@ant-design/pro-components";
import { useSearchParams } from "react-router";
import { getSorterParams, getUserRequestPath } from "../../../../utils";
import DeleteReadingsButton from "./actions";
import DeleteReadingButton from "./actions";
import { downloadFile } from "../../../../hooks/file";
import { useMiddahTableGet } from "../../../../hooks/api/middot";
import { LazySelect } from "../../../Common/SelectLazyLoader";
import { useAuth, UserRoleType } from "../../../../store/auth";

export const TYPE_COLORS: Record<string, string> = {
  pdf: "#F5222D",
  doc: "#1677FF",
  docx: "#1677FF",
  xls: "#52C41A",
  xlsx: "#52C41A",
  ppt: "#FAAD14",
  pptx: "#FAAD14",
  txt: "#8C8C8C",
  rtf: "#8C8C8C",
  csv: "#13C2C2",
};

function ToolBar({ onCreate }: { onCreate: () => void }) {
  const { user } = useAuth();
  const isAdmin = user?.role
    ? getUserRequestPath(user.role as UserRoleType) === "admin"
    : false;

  if (!isAdmin) return [];

  return [
    <Button
      key="create"
      icon={<PlusCircleOutlined />}
      type="primary"
      onClick={(e) => {
        e.preventDefault();
        onCreate();
      }}
    >
      Upload Reading
    </Button>,
  ];
}

export default function ReadingsTab(): React.ReactNode | null {
  const { user } = useAuth();
  const [, setSearchParams] = useSearchParams();
  const [tableKey, setTableKey] = useState<number>(() => Date.now());
  const { t, i18n } = useTranslation();
  const readingTableGet = useReadingTableGet();
  const readingTypesGet = useReadingTypesGet();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [editingRecord, setEditingRecord] = useState<ReadingTableRow | null>(
    null,
  );
  const middahTableGet = useMiddahTableGet();
  const isAdmin = user?.role
    ? getUserRequestPath(user.role as UserRoleType) === "admin"
    : false;

  const fetchTypeOptions = async (params: {
    search?: string;
    page: number;
    pageSize: number;
  }) => {
    const response = await readingTypesGet.fetch();
    return response || [];
  };
  const mapTypeOption = (item: string) => ({
    label: item.toUpperCase(),
    value: item,
  });

  const fetchMiddahOptions = async (params: {
    search?: string;
    page: number;
    pageSize: number;
  }) => {
    const response = await middahTableGet.fetch({
      page: params.page,
      pageSize: params.pageSize,
      search: params.search,
    });
    return response?.data || [];
  };

  const mapMiddahOption = (item: any) => ({
    label: item.name,
    value: item.id,
  });

  useEffect(() => {
    setTableKey(Date.now());
  }, []);

  const handleCreate = () => {
    setModalMode("create");
    setEditingRecord(null);
    setModalOpen(true);
  };

  const handleEdit = (record: ReadingTableRow) => {
    setModalMode("edit");
    setEditingRecord(record);
    setModalOpen(true);
  };

  const handleSave = () => {
    setSearchParams({}, { replace: true });
    setTableKey(Date.now());
    setModalOpen(false);
  };

  const columns: ProColumns<ReadingTableRow>[] = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      valueType: "select",
      hideInSearch: true,
      render: (_, record) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <FileTextOutlined />
          {record.fileLink ? (
            <a
              href={record.fileLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "underline", color: "#262626" }}
            >
              {record.name}
            </a>
          ) : (
            <span style={{ color: "#262626" }}>{record.name}</span>
          )}
        </span>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      sorter: true,
      hideInSearch: true,
      render: (_, record) => (
        <Tag
          variant="outlined"
          color={TYPE_COLORS[record.type.toLowerCase()] || "default"}
          style={{ textTransform: "uppercase", fontSize: 12 }}
        >
          {record.type}
        </Tag>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      hideInTable: true,
      renderFormItem: () => (
        <LazySelect
          mode="multiple"
          fetchOptions={fetchTypeOptions}
          mapOption={mapTypeOption}
        // pageSize={20}
        />
      ),
    },
    {
      title: "Tags",
      dataIndex: "tags",
      key: "tags",
      valueType: "select",
      hideInSearch: true,
      render: (_, record) => {
        const allTags = record.middahs || [];
        const visibleTags = allTags.slice(0, 3);
        const hiddenCount = allTags.length - 3;

        return (
          <span
            style={{
              display: "flex",
              gap: 6,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {visibleTags.map((tag) => (
              <Tag color="#722ED1" variant="outlined" key={tag.id}>
                {tag.name}
              </Tag>
            ))}
            {hiddenCount > 0 && <span>+{hiddenCount}</span>}
          </span>
        );
      },
    },
    {
      title: "Tags",
      dataIndex: "middahIds",
      key: "middahIds",
      valueType: "select",
      hideInTable: true,
      renderFormItem: () => (
        <LazySelect
          mode="multiple"
          fetchOptions={fetchMiddahOptions}
          mapOption={mapMiddahOption}
          pageSize={20}
        />
      ),
    },
    {
      title: "Global",
      dataIndex: "globalContent",
      key: "globalContent",
      align: "center",
      hideInSearch: true,
      render: (_, record) =>
        record.globalContent ? (
          <CheckCircleFilled style={{ color: "#52C41A", fontSize: 16 }} />
        ) : (
          <MinusOutlined style={{ color: "#BFBFBF" }} />
        ),
    },
    {
      title: "Upload Date",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      hideInSearch: true,
      render: (_, record) => {
        const date = dayjs(record.createdAt);
        return date.isValid() ? date.format("MMMM D, YYYY") : "-";
      },
    },
    {
      title: "Upload Date",
      dataIndex: "uploadDate",
      key: "uploadDate",
      hideInTable: true,
      valueType: "dateRange",
      fieldProps: { format: "MM-DD-YYYY" },
      search: {
        transform: (value?: [string, string]) => {
          if (!value || value.length !== 2) return {};
          return {
            createdAtFrom: value[0],
            createdAtTo: value[1],
          };
        },
      },
    },
  ];

  const tableRequest = (
    {
      current,
      pageSize,
      ...args
    }: Record<string, string> & {
      pageSize?: number;
      current?: number;
      keyword?: string;
    },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<ReadingTableRow>>> => {
    const newParams: ReadingGetParams = {
      page: current || 1,
      pageSize: pageSize || 10,
      orderByColumn: "createdAt",
      orderBy: "DESC",
      ...args,
      ...getSorterParams(sorter),
    };

    setSearchParams({ ...args, ...getSorterParams(sorter) }, { replace: true });

    return readingTableGet.fetch(newParams).then((response) => {
      if (response) {
        const { data, total } = response;
        return {
          data: data || [],
          success: true,
          total,
        };
      }
      return { data: [], success: false, total: 0 };
    });
  };

  const actions: any[] = [
    [
      "download",
      ({ record }: { record: ReadingTableRow }) => ({
        title: "Download",
        onClick: (e: MouseEvent) => {
          e.stopPropagation();
          downloadFile(record.fileLink, record.name);
        },
      }),
    ],
    ...(isAdmin
      ? [
        [
          "edit",
          ({ record }: { record: ReadingTableRow }) => ({
            title: "Edit",
            onClick: (e: MouseEvent) => {
              e.stopPropagation();
              handleEdit(record);
            },
          }),
        ],
        [DeleteReadingButton, { setTableKey }],
      ]
      : []),
  ];

  return (
    <>
      <Table<ReadingTableRow>
        key={`readings-${tableKey}`}
        resetKey={() => setTableKey(Date.now())}
        className="tableCustomPadding"
        headerTitle={t("pages.readings.title")}
        request={tableRequest}
        toolBarRender={() => [
          <ToolBar key="toolbar" onCreate={() => handleCreate()} />,
        ]}
        columns={columns}
        onReset={() => {
          setSearchParams({}, { replace: true });
          setTableKey(Date.now());
        }}
        actions={actions}
        search={{
          style: {
            border: "1px solid #0000000F",
            borderRadius: 8,
            padding: 24,
          },
        }}
        options={{
          search: {
            name: "search",
            placeholder: "Search by reading name",
            style: { width: 280 },
          },
        }}
        columnsState={{
          persistenceKey: "pro-table-readings",
          persistenceType: "localStorage",
        }}
      />
      <ReadingModal
        open={modalOpen}
        mode={modalMode}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        recordId={editingRecord ? editingRecord.id : undefined}
      />
    </>
  );
}
